/**
 * Mailbox: local state machine (reducer + pure guard-rail helpers).
 *
 * The whole section runs on mock state today: the section component holds this reducer, and the
 * simulated async actions (create, reset) wait a realistic delay, then dispatch the result. The reducer itself is PURE: no timers, no
 * `Date.now()`, no randomness. Timestamps and generated values arrive as action payloads, so
 * every transition and guard rail below is unit-testable without rendering (see `state.test.ts`).
 * Shapes mirror the eventual provider APIs, so mock -> real is a data-source swap, not a reshape.
 *
 * No React and no copy in here: helpers that need translation take `t` as a parameter.
 */
import type { TFunction } from 'i18next';

import {
  DEFAULT_MAILBOX_QUOTA_MB,
  isMailboxError,
  MAILBOX_FALLBACK_DOMAIN,
  mailboxEmail,
  type DomainStatus,
  type MailboxDomain,
  type MailboxEntitlement,
  type MailboxPriceRead,
  type MailboxRecord,
  type MailboxUsage,
} from '../../mock-data/mailbox-records';
import { currencySymbol, formatAmountWithCode } from '../../utils/money';
import type { CreateMailboxBody } from './api/client';

// --- Validation ----------------------------------------------------------------

/** Why a proposed mailbox local part was rejected. Each maps to a specific message in the UI. */
export type MailboxLocalPartError = 'empty' | 'invalid' | 'duplicate' | 'reserved' | 'crm';

/**
 * The local part the service gives the brand's SYSTEM mailbox on every domain: `support@`, which the
 * casino's CRM mail arrives on. Taken from the first create, so a create for it would only meet the
 * duplicate refusal, whose message ("rotate its password instead") makes no sense to an operator who
 * has never seen it. Refused here instead, with its own reason (`crm`), apart from the RFC names.
 */
export const MAILBOX_CRM_LOCAL_PART = 'support';

/**
 * RFC 2142 / provider-claimed local parts a mailbox may not use: a provider rejects these at create
 * time, so catching it in the client turns a failed provision into an inline message.
 */
export const MAILBOX_RESERVED_LOCAL_PARTS: readonly string[] = [
  'postmaster',
  'abuse',
  'hostmaster',
  'webmaster',
  'noc',
  'security',
];

/**
 * Validate a proposed mailbox local part (the part before `@brandDomain`). Guard rails, in order:
 * present -> valid characters (letters, numbers, dots and dashes, no leading/trailing dot) -> not the
 * CRM's `support` -> not a reserved name -> not already a mailbox on this brand (case-insensitive). `duplicate` compares the
 * full address the name would make on `domain` against each row's `email`, and matches a row in ANY
 * status, `deleted` included: the provider answers a create for an existing address with a
 * conflict, so the local check mirrors that rather than inviting a doomed request.
 * Returns the reason, or `null` when the local part is acceptable.
 */
export const validateMailboxLocalPart = (
  value: string,
  existing: readonly Pick<MailboxRecord, 'email'>[],
  domain: string,
): MailboxLocalPartError | null => {
  const v = value.trim().toLowerCase();
  if (!v) return 'empty';
  // Letters, numbers, dots and dashes; must start and end with a letter or number (no leading /
  // trailing dot or dash).
  if (!/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(v)) return 'invalid';
  if (v === MAILBOX_CRM_LOCAL_PART) return 'crm';
  if (MAILBOX_RESERVED_LOCAL_PARTS.includes(v)) return 'reserved';
  const email = mailboxEmail(v, domain);
  if (existing.some((m) => m.email.toLowerCase() === email)) return 'duplicate';
  return null;
};

/** The password bounds the service enforces: at least 8 (the client contract's floor) and at most
 *  54 (the provider's limit for a plaintext password). */
export const MAILBOX_PASSWORD_MIN = 8;
export const MAILBOX_PASSWORD_MAX = 54;

/** Why a proposed password was rejected locally. Each maps to a specific message in the UI. */
export type MailboxPasswordError = 'short' | 'long' | 'edge-whitespace';

/**
 * The password rules the SERVICE sets, checked here so the operator hears about them before the
 * round trip: 8 to 54 characters, no whitespace at the start or end. Interior spaces are fine (a
 * passphrase is legitimate). Deliberately NOT here: any strength rule. The provider owns those and
 * refuses with a `400` whose `message` names the broken rule; the form shows that message verbatim
 * rather than guessing at the rules and drifting from them. Returns `null` when acceptable.
 */
export const validateMailboxPassword = (value: string): MailboxPasswordError | null => {
  if (value.length < MAILBOX_PASSWORD_MIN) return 'short';
  if (value.length > MAILBOX_PASSWORD_MAX) return 'long';
  if (value !== value.trim()) return 'edge-whitespace';
  return null;
};

// --- Billing -------------------------------------------------------------------

/**
 * Whether the NEXT mailbox created takes a `paid` slot, from the local entitlement: the MOCK path's
 * rule (the live path reads the list instead, see `nextMailboxIsPaidByList`). There is no cap, so
 * this never blocks creation; it only decides whether the create flow routes through checkout. The
 * included allowance is free; everything at or beyond it is paid.
 */
export const nextMailboxIsPaid = (entitlement: MailboxEntitlement): boolean => entitlement.used >= entitlement.included;

/**
 * Whether the NEXT mailbox created takes a `paid` slot, from the service's list: the LIVE path's
 * rule, as the contract writes it ("no non-system mailbox yet: the next one is free"). Free while
 * the list holds no live free mailbox of the OWNER's, paid once it does. `support@` (role `system`)
 * reads `free` too and never counts: it exists from the first create, so counting it would charge
 * the owner for their own first mailbox. Never both, because it reads what exists.
 */
export const nextMailboxIsPaidByList = (
  mailboxes: readonly Pick<MailboxRecord, 'billing' | 'status' | 'role'>[],
): boolean => mailboxes.some((m) => m.billing === 'free' && m.role !== 'system' && m.status !== 'deleted');

/**
 * Whether the brand wallet can settle a charge of `amount`. `balance` is null until the wallets
 * have loaded, and `matched` is false when the resolved MAIN wallet is not in the price's currency.
 * Never charge a wallet you have not confirmed: null / unmatched is `false`, and the caller falls
 * back to a card.
 */
export const mailboxWalletCovers = (balance: number | null, matched: boolean, amount: number): boolean =>
  matched && balance !== null && balance >= amount;

/**
 * What the section may say about the price of a paid mailbox, from the service's answer and nothing
 * else (see `MailboxPrice`):
 *  - `unknown`: not answered yet, or the read failed (`failed`). No number is shown: a dash, or the
 *    line without its number. A wrong price on screen is worse than a missing one.
 *  - `free`: the service answered `0`, so charging is switched off. Never shown as "0": the section
 *    says the mailbox is free of charge.
 *  - `priced`: the amount in the service's own currency, in the app's money format
 *    (`currencySymbol` + `formatAmountWithCode`, "$10.00 USD"). No period: it is charged once.
 */
export type MailboxPriceView =
  | { kind: 'unknown'; failed: boolean }
  | { kind: 'free' }
  | { kind: 'priced'; amount: number; currency: string; label: string };

export const mailboxPriceView = (read: MailboxPriceRead): MailboxPriceView => {
  if (read.status !== 'ready') return { kind: 'unknown', failed: read.status === 'failed' };
  const { amount, currency } = read.price;
  if (amount === 0) return { kind: 'free' };
  return { kind: 'priced', amount, currency, label: `${currencySymbol(currency)}${formatAmountWithCode(amount, currency, 2)}` };
};

// --- Storage formatting (pure, for the tile's storage cell + bar) ---------------

const GIB = 1024 ** 3;
const MIB = 1024 ** 2;
const KIB = 1024;

/** One byte count as a human unit: one decimal GB at or above a GiB (trailing ".0" trimmed, so
 *  "4 GB" not "4.0 GB"), whole MB at or above a MiB, whole KB below it (a mailbox that has received
 *  a handful of messages reads "26 KB", not "0 MB"). Exported so the add-tile can say "5 GB each"
 *  off the quota constant instead of a literal. */
export const formatMailboxQuota = (bytes: number): string => {
  if (bytes >= GIB) {
    const gb = (bytes / GIB).toFixed(1);
    return `${gb.endsWith('.0') ? gb.slice(0, -2) : gb} GB`;
  }
  if (bytes >= MIB) return `${Math.round(bytes / MIB)} MB`;
  return `${Math.round(bytes / KIB)} KB`;
};

/**
 * The storage value: "3.2 GB of 5 GB" when the provider reports a ceiling, "26 KB used" when it
 * does not. An absent `bytesMax` means UNKNOWN, never zero, so there is no "of" with a dash and no "of 0":
 * the ceiling is simply not stated.
 */
export const formatMailboxStorage = (bytesUsed: number, bytesMax: number | undefined, t: TFunction): string =>
  bytesMax === undefined
    ? t('{{used}} used', { used: formatMailboxQuota(bytesUsed) })
    : t('{{used}} of {{max}}', { used: formatMailboxQuota(bytesUsed), max: formatMailboxQuota(bytesMax) });

/** 0..1 for the bar's width and its near-full styling. Clamps to the range, and returns 0 rather
 *  than NaN when `bytesMax` is 0. Only for a reported ceiling: with none there is no bar to fill. */
export const mailboxStorageRatio = (bytesUsed: number, bytesMax: number): number => {
  if (bytesMax <= 0) return 0;
  return Math.min(1, Math.max(0, bytesUsed / bytesMax));
};

/** The fill at or above which the storage bar turns warning-coloured. The threshold is the only
 *  reason the bar exists instead of plain text, so it lives here where a test can pin it. */
export const MAILBOX_STORAGE_WARN_RATIO = 0.85;

/** The storage bar's tone: `warning` at or above {@link MAILBOX_STORAGE_WARN_RATIO}, `info` below. */
export const mailboxStorageTone = (bytesUsed: number, bytesMax: number): 'info' | 'warning' =>
  mailboxStorageRatio(bytesUsed, bytesMax) >= MAILBOX_STORAGE_WARN_RATIO ? 'warning' : 'info';

// --- The primary action: "the action carries the state" ------------------------

/**
 * What the ONE primary control on a mailbox page's header does. (The tile derives its Webmail from
 * the same provider status, through `mailboxShowsStatusPill`.)
 */
export type MailboxPrimaryAction =
  /** Healthy: `active`. The control opens the inbox. */
  | { kind: 'open-webmail' }
  /** Nothing the operator can do here: `suspended` or `deleted` at the provider (neither has an
   *  operator fix in the service). */
  | { kind: 'none' };

/**
 * The primary action for a mailbox: the provider status decides it, and only `active` has one.
 *
 * `suspended` has no action of its own: the provider sets and clears it, and the service offers no
 * suspend or resume, so there is nothing for the operator to press. It keeps a status pill
 * ({@link mailboxShowsStatusPill}) so a suspended mailbox is still legible. A `deleted` mailbox has
 * no inbox to open.
 */
export const mailboxPrimaryAction = (mailbox: MailboxRecord): MailboxPrimaryAction => {
  switch (mailbox.status) {
    case 'suspended':
    case 'deleted':
      return { kind: 'none' };
    case 'active':
      return { kind: 'open-webmail' };
    default: {
      const unhandled: never = mailbox.status;
      return unhandled;
    }
  }
};

/**
 * Whether a mailbox keeps a status badge. Only the provider statuses with no action of their own do
 * (`suspended`, `deleted`); on an `active` mailbox the primary action carries the state and a badge
 * would say the same thing twice.
 */
export const mailboxShowsStatusPill = (mailbox: Pick<MailboxRecord, 'status'>): boolean =>
  mailbox.status === 'suspended' || mailbox.status === 'deleted';

// --- Status line -----------------------------------------------------------------

/** The tone of the one-line status under a mailbox's name. */
export type MailboxStatusTone = 'dead' | 'warn';

/**
 * The status line's tone, or `null` for a healthy mailbox: `null` means "no problem", and the line
 * then shows the plan (Free or Paid) instead of a status. Follows the provider status, as
 * {@link mailboxPrimaryAction} does, so the line and the button never contradict: deleted is
 * `dead`, suspended is `warn`. The copy itself lives in the components.
 */
export const mailboxStatusTone = (mailbox: MailboxRecord): MailboxStatusTone | null => {
  switch (mailbox.status) {
    case 'deleted':
      return 'dead';
    case 'suspended':
      return 'warn';
    case 'active':
      return null;
    default: {
      const unhandled: never = mailbox.status;
      return unhandled;
    }
  }
};

// --- The domain ------------------------------------------------------------------

/**
 * Whether the domain's mail is WORKING: the one question every "does mail work" decision asks, read
 * from the service's `ready` and never from a status comparison (the status list grows; `ready`
 * keeps its meaning). `null` (a domain the service has never heard of) is not ready.
 */
export const mailboxDomainReady = (domain: MailboxDomain | null): boolean => domain?.ready === true;

/**
 * The statuses that END the wait without mail working: out of the race, each with its own message
 * (`failed`, `suspended`), or settled (`dns_verified`: the provider sees correct DNS and the domain is
 * ready for a mailbox; the last delete walks back to it and only a create moves it on). Not a list of
 * waiting states: every other status waits, so one the service adds later waits too.
 */
const DOMAIN_WAIT_ENDED: ReadonlySet<DomainStatus> = new Set<DomainStatus>(['failed', 'suspended', 'dns_verified']);

/**
 * THE ONE RULE for "this domain is still being set up": from T0 until its mail works (the service's
 * `ready`), unless the domain left the race or settled (see {@link DOMAIN_WAIT_ENDED}). T0 is the
 * first create, and its evidence is a live mailbox on the domain: a domain registered without a mail
 * order (most never get one) sits at `pending` with no mailbox, and nothing moves it on until the
 * operator's first create, so it is not waiting for anything. It reads `ready` and never a list of
 * waiting statuses: listing them is what left a state uncovered twice (the notice without `pending`,
 * the notice and the cards without `order_created`), and the service has added statuses before. A
 * status the client does not know reads as `pending`, so it waits.
 *
 * Read by everything that says the domain is not ready yet, so none of them can contradict another:
 * the section's "incoming mail is on its way" notice, the waiting line and disabled Webmail on every
 * card and on the mailbox page, support@'s Pending card while it is shown (`mailboxShowsPending`) and the domain poll
 * (`mailboxDomainPolls`), so the notice can never outlive the poll that clears it.
 *
 * Until then a mailbox cannot receive: Webmail is disabled, the page holds the client guide, the server
 * table and (once the one-time password has been seen) the credentials back, and each says why; Setup
 * Mailbox stays enabled but only opens that page, which keeps the trash reachable. A client set up
 * during the wait receives nothing and reads as our defect, not as DNS still propagating. This
 * DEPARTS from frontend-integration-main.md section 3 ("`ready: false` does not mean unusable") on
 * purpose. A `suspended` domain is also not ready, but its mailboxes exist and hold mail, so their
 * credentials stay reachable; a `failed` one says why and hides nothing (see
 * `mailboxDomainBlocksActions`).
 */
export const mailboxDomainAwaitingSetup = (
  domain: MailboxDomain | null,
  mailboxes: readonly Pick<MailboxRecord, 'status'>[],
): boolean =>
  domain !== null && hasLiveMailbox(mailboxes) && !mailboxDomainReady(domain) && !DOMAIN_WAIT_ENDED.has(domain.status);

/**
 * Whether the section polls its domain: exactly while it is being set up ({@link
 * mailboxDomainAwaitingSetup}). There are no webhooks to say when it moves on; once it is ready, out of
 * the race or settled there is nothing to wait for, and polling would re-read it every interval for
 * as long as the section is open.
 */
export const mailboxDomainPolls = (domain: MailboxDomain | null, mailboxes: readonly Pick<MailboxRecord, 'status'>[]): boolean =>
  mailboxDomainAwaitingSetup(domain, mailboxes);

/**
 * How often the section re-reads its domain while it is still being set up (`mailboxDomainPolls`).
 * There are no webhooks, so this is how the section learns setup has finished; the provider's guidance is every 15 to 30 s.
 * Real behaviour, not a mock timing: wiring the endpoint replaces only the read inside the loop.
 */
export const MAILBOX_DOMAIN_POLL_MS = 20_000;

/**
 * How long a create may take before the section stops waiting and returns the operator to the form.
 * The live round trip is about 4s (the provisioning screen's 5s run covers it); a gateway that gives
 * up answers on its own well before this. What this catches is the create that NEVER answers, which
 * would otherwise leave the provisioning screen's last row spinning forever. It can stop waiting on a
 * create the provider then completes: the operator's retry is a `409`, whose rotation offer is the
 * way forward.
 */
export const MAILBOX_CREATE_TIMEOUT_MS = 30_000;

/**
 * Whether the domain's state takes every mailbox action away. Only `failed`: the mail order did not
 * go through, so there is nothing to open, set up, create, pay for or delete until it is fixed.
 * `dns_pending` blocks nothing: the mailboxes sign in and send while incoming mail propagates, and
 * neither does an unknown domain (`null`): the first create is what registers it.
 */
export const mailboxDomainBlocksActions = (domain: MailboxDomain | null): boolean => domain?.status === 'failed';

/** Whether any mailbox is LIVE (not `deleted`): what the domain's `active` rests on. */
const hasLiveMailbox = (mailboxes: readonly Pick<MailboxRecord, 'status'>[]): boolean =>
  mailboxes.some((m) => m.status !== 'deleted');

// --- Dates -----------------------------------------------------------------------------

/**
 * Compact "2h ago" / "23d ago" from an ISO timestamp: minutes below an hour, hours below a day,
 * days beyond. Under a minute (and any future or unparseable timestamp, so a clock skew never
 * yields a negative) reads "Just now". Interpolates through i18n with `{{n}}` (never `{{count}}`)
 * so no plural suffix leaks and the unit stays translatable. The four strings are the ones the
 * area already translates, so this mints no new key. `now` is injectable for tests.
 */
export const mailboxTimeAgoLabel = (iso: string, t: TFunction, now: number = Date.now()): string => {
  const elapsedMs = now - new Date(iso).getTime();
  if (!Number.isFinite(elapsedMs) || elapsedMs < 60_000) return t('Just now');
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 60) return t('{{n}}m ago', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('{{n}}h ago', { n: hours });
  return t('{{n}}d ago', { n: Math.floor(hours / 24) });
};

// --- Reducer -----------------------------------------------------------------------------

export type MailboxesState = {
  mailboxes: MailboxRecord[];
  entitlement: MailboxEntitlement;
  /** The section's one domain (`GET /domains/{domain}`), whose status carries DNS setup. `null` is
   *  a `404`: the service has never heard of the domain, which is NOT the same as a registered
   *  domain with no mailboxes (`[]`). */
  mailDomain: MailboxDomain | null;
  /**
   * The plaintext password per mailbox address. Transient BY DESIGN: never stored on `MailboxRecord` and
   * never persisted (see `store.ts`). The real provider returns this value at create and at reset
   * and never again, so "present in this map" means visible-this-session and "absent" means
   * masked, exactly as the real credential behaves. It is a LOGIN password and resettable at will,
   * so absence here is a non-event rather than a loss.
   */
  secrets: Record<string, string>;
};

export type MailboxesAction =
  /** Re-seed the whole section (the brand switched or storage was read): replace mailboxes,
   *  entitlement and the domain, and clear `secrets` so one brand's password can never leak into
   *  another. */
  | { type: 'hydrate'; mailboxes: MailboxRecord[]; entitlement: MailboxEntitlement; mailDomain: MailboxDomain | null }
  /** A domain poll answered: take the domain as the provider reports it. */
  | { type: 'mailDomainResolved'; mailDomain: MailboxDomain }
  /** Create a mailbox and increment `entitlement.used`. Its slot (`free` or `paid`) is the one the
   *  section decided before the create (no cap ever blocks it), so the row, the add card and the
   *  checkout agree. Creation is SYNCHRONOUS at the provider: this action fires only once the create
   *  returned, so the row is born `active` and can sign in and send at once, with the password it
   *  was created with (set at `createdAt`, never rotated). The flow is pay, then create, so a paid
   *  mailbox is already paid for when it arrives. The timestamp arrives as payload so the reducer
   *  stays pure. */
  | {
      type: 'create';
      /** The full address; the reducer stores it lowercase. */
      email: string;
      domain: string;
      createdAt: string;
      /** The slot the section decided for this create. */
      billing: 'free' | 'paid';
      /** The invoice settled for this purchase. Present whenever the mailbox is paid. */
      invoiceId?: string;
      /** The domain as the create left it, when the service said (the live source's answer). Absent,
       *  it is derived here (see `domainAfterCreate`). */
      mailDomain?: MailboxDomain;
      /** The MOCK's first create: the service makes the brand's support@ (role `system`) in the same
       *  call, so the mock lists it too. The live source never sets this: its list comes from the
       *  service, which already has support@, re-read on the section's next mount. */
      withSystemMailbox?: boolean;
    }
  /** Remove a mailbox entirely: drop it, decrement `entitlement.used`, and clear its secret. */
  | { type: 'remove'; email: string }
  /** A password was issued after creation (a reset, or the first one on a mailbox that had none):
   *  stamp its date (a date, not a secret) so "Password set on {date}" survives navigation once the
   *  plaintext is gone, and count the rotation when it replaced one. */
  | { type: 'passwordIssued'; email: string; setAt: string }
  /** Write one mailbox's plaintext password into the transient map. */
  | { type: 'setSecret'; email: string; value: string }
  /** Delete one mailbox's plaintext password from the transient map. */
  | { type: 'clearSecret'; email: string };

/**
 * The domain once a create has returned. The mirror of the walk back in `remove`: a verified domain
 * with a mailbox on it is `active`. An UNKNOWN domain (`null`, a `404`) is registered by the create
 * itself: the one call registers it, orders the service and publishes DNS, and answers with the
 * domain `dns_pending` (so the section's notice and poll take over from there). Any other status
 * (still waiting on DNS, failed) is the provider's to move.
 */
const domainAfterCreate = (domain: MailboxDomain | null, name: string): MailboxDomain => {
  if (domain === null) return { domain: name.trim().toLowerCase(), status: 'dns_pending', ready: false, manageDns: true };
  return domain.status === 'dns_verified' ? { ...domain, status: 'active', ready: true } : domain;
};

/** Map a transition over the one mailbox at `email`, leaving the rest untouched. */
const mapMailbox = (
  mailboxes: MailboxRecord[],
  email: string,
  fn: (mailbox: MailboxRecord) => MailboxRecord,
): MailboxRecord[] => mailboxes.map((mailbox) => (mailbox.email === email ? fn(mailbox) : mailbox));

/** Drop one key from the secrets map, returning a new object. */
const withoutSecret = (secrets: Record<string, string>, email: string): Record<string, string> => {
  const { [email]: _dropped, ...rest } = secrets;
  void _dropped;
  return rest;
};

export const mailboxesReducer = (
  state: MailboxesState,
  action: MailboxesAction,
): MailboxesState => {
  switch (action.type) {
    case 'hydrate':
      return { mailboxes: action.mailboxes, entitlement: action.entitlement, mailDomain: action.mailDomain, secrets: {} };
    case 'mailDomainResolved':
      return { ...state, mailDomain: action.mailDomain };
    case 'create': {
      // No cap check: creation is never blocked, only priced. The slot comes with the action (the
      // section's one rule). Born `active` (see the action's comment).
      const paid = action.billing === 'paid';
      // `owner` is the operator's first mailbox on the DOMAIN: a create is the owner only when no live
      // mailbox of theirs is left. A `deleted` row the provider still lists does not hold the role,
      // and neither does support@ (role `system`), which exists from the first create.
      const role: MailboxRecord['role'] = state.mailboxes.some((m) => m.status !== 'deleted' && m.role !== 'system')
        ? 'user'
        : 'owner';
      const next: MailboxRecord = {
        email: action.email.trim().toLowerCase(),
        domain: action.domain.trim().toLowerCase(),
        role,
        status: 'active',
        passwordSetAt: action.createdAt,
        passwordRotations: 0,
        billing: action.billing,
        createdAt: action.createdAt,
        ...(paid && action.invoiceId ? { invoiceId: action.invoiceId } : {}),
      };
      // support@ first, as the service lists it, and only once: it holds no seat.
      const system: MailboxRecord[] =
        action.withSystemMailbox && !state.mailboxes.some((m) => m.role === 'system')
          ? [
              {
                email: mailboxEmail(MAILBOX_CRM_LOCAL_PART, next.domain),
                domain: next.domain,
                role: 'system',
                status: 'active',
                passwordRotations: 0,
                billing: 'free',
                createdAt: action.createdAt,
              },
            ]
          : [];
      return {
        ...state,
        mailboxes: [...system, ...state.mailboxes, next],
        entitlement: { ...state.entitlement, used: state.entitlement.used + 1 },
        mailDomain: action.mailDomain ?? domainAfterCreate(state.mailDomain, action.domain),
      };
    }
    case 'remove': {
      // A `deleted` row was never a seat (it is not counted in `used`), so removing one must not
      // give an allowance back; only a live mailbox frees one.
      const target = state.mailboxes.find((m) => m.email === action.email);
      const freesSeat = target !== undefined && target.status !== 'deleted';
      const mailboxes = state.mailboxes.filter((m) => m.email !== action.email);
      // THE WALK BACK: deleting the last live mailbox moves a READY domain back to `dns_verified`,
      // not ready, as the provider does. A section that kept saying mail works with no mailbox on
      // the domain would be telling the operator something false.
      const walkBack = mailboxDomainReady(state.mailDomain) && !hasLiveMailbox(mailboxes);
      return {
        ...state,
        mailboxes,
        mailDomain:
          walkBack && state.mailDomain ? { ...state.mailDomain, status: 'dns_verified', ready: false } : state.mailDomain,
        entitlement: freesSeat ? { ...state.entitlement, used: Math.max(0, state.entitlement.used - 1) } : state.entitlement,
        // Drop the plaintext too, or a removed mailbox leaves a live password in memory.
        secrets: withoutSecret(state.secrets, action.email),
      };
    }
    case 'passwordIssued':
      return {
        ...state,
        mailboxes: mapMailbox(state.mailboxes, action.email, (m) => ({
          ...m,
          passwordSetAt: action.setAt,
          // A first password on a mailbox that had none replaces nothing, so it is not a rotation.
          passwordRotations: m.passwordSetAt ? m.passwordRotations + 1 : m.passwordRotations,
        })),
      };
    case 'setSecret':
      return { ...state, secrets: { ...state.secrets, [action.email]: action.value } };
    case 'clearSecret':
      return { ...state, secrets: withoutSecret(state.secrets, action.email) };
    default: {
      // Compile-time exhaustiveness guard AND a referential runtime no-op: an unknown action
      // returns the SAME state object, while a new action type without a branch fails to compile.
      const unhandled: never = action;
      void unhandled;
      return state;
    }
  }
};

// --- The Messages card ---------------------------------------------------------

/**
 * What the Messages card's ONE line under the count says, or `null` when it has nothing to report
 * (the card is then just the number). Each half only when it says something: when mail last arrived
 * (an absent `lastReceivedAt` proves nothing: it is INBOX-only and moves backwards when mail is
 * filed), and that unread mail is waiting (only `unreadMail === true`; false or absent is not news).
 */
export const mailboxMessagesLine = (
  usage: Pick<MailboxUsage, 'lastReceivedAt' | 'unreadMail'> | undefined,
): { lastReceivedAt: string | null; unread: boolean } | null => {
  const lastReceivedAt = usage?.lastReceivedAt ? usage.lastReceivedAt : null;
  const unread = usage?.unreadMail === true;
  return lastReceivedAt || unread ? { lastReceivedAt, unread } : null;
};

// --- The create request --------------------------------------------------------

/**
 * `POST /domains/{domain}/owner-mailbox`'s body, the whole of it: the address, `quotaMb` (megabytes)
 * and `password` (the form's generated one, or the operator's own: a strong password is ours to
 * guarantee, since the provider's own rules accepted `123456789A`) on every create, and `manageDns` on
 * the domain's FIRST mailbox only (it is what registers the domain). The casino, tenant, site and
 * owner are resolved server-side from the owner's token and are never sent.
 */
export const createMailboxBody = (email: string, password: string, firstCreate: boolean): CreateMailboxBody => ({
  email,
  quotaMb: DEFAULT_MAILBOX_QUOTA_MB,
  password,
  ...(firstCreate ? { manageDns: true } : {}),
});

/**
 * Whether the domain poll stays stopped on an auth refusal: the last read was refused for the OWNER
 * (`unauthorized`: the session no longer resolves; `forbidden`: the domain is not theirs), for THIS
 * brand, and the store still holds the token that read went out with. Neither refusal resolves by
 * waiting, so the poll does not re-ask on it; a refreshed or new token resumes it.
 */
export const mailboxPollHaltedByAuth = (
  failure: { brandId: string | undefined; error: { code: string } | null; token: string | null } | null,
  brandId: string | undefined,
  token: string | null,
): boolean =>
  failure !== null &&
  failure.brandId === brandId &&
  failure.token === token &&
  (failure.error?.code === 'unauthorized' || failure.error?.code === 'forbidden');

/**
 * Whether the LIVE mailbox list re-reads after an auth refusal: its last read was refused for the
 * owner (`unauthorized` / `forbidden`) and the store now holds a DIFFERENT token than that read went
 * out with (refreshed by any other call, or a new sign-in). The list's side of the poll's recovery
 * ({@link mailboxPollHaltedByAuth}): a refusal holds while its token does, and a new token asks once.
 * A load that never stamped a token (no read went out, e.g. no domain on record) never re-reads.
 */
export const mailboxListReloadsOnNewToken = (
  load: { failed: boolean; error: { code: string } | null; token?: string | null },
  brandId: string | undefined,
  token: string | null,
): boolean =>
  load.failed &&
  load.token !== undefined &&
  (load.error?.code === 'unauthorized' || load.error?.code === 'forbidden') &&
  !mailboxPollHaltedByAuth({ brandId, error: load.error, token: load.token }, brandId, token);

// --- The brand's support mailbox, while it is being made -------------------------

/**
 * Whether a mailbox shows as PENDING in the grid: the brand's SYSTEM mailbox (`support@`) while its
 * domain is being set up, by the one rule ({@link mailboxDomainAwaitingSetup}), as the service asks
 * ("setting up while false, working once true"). It exists from the first create, before any DNS is
 * published, and while it is shown (the `mailbox-support` flag) it is the only mailbox an operator
 * can see in that window, so it is the one whose state they could misread: what is pending is the
 * DOMAIN, not the mailbox. Otherwise (ready, failed, suspended, settled) it is an ordinary card. With
 * the switch off the grid never renders it, and this rule waits, unchanged, for the switch.
 */
export const mailboxShowsPending = (domain: MailboxDomain | null, mailbox: Pick<MailboxRecord, 'role' | 'status'>): boolean =>
  // support@ itself, listed and live, is the evidence of T0.
  mailbox.role === 'system' && mailboxDomainAwaitingSetup(domain, [mailbox]);

// --- What the operator sees ------------------------------------------------------

/**
 * Whether the operator SEES a mailbox: every one, except the brand's system mailbox (`support@`)
 * while `show` is `false`. `show` is the `mailbox-support` flag, off for operators and revealed by the
 * "777supportmailbox" code. RENDERING ONLY: the grid, the heading count and the mailbox page read it,
 * and no rule does. The setting-up rule, the poll, the free slot and the create form keep the FULL list,
 * support@ included: at T0 it is the domain's only live mailbox, and a list without it would hide the
 * notice and stop the poll.
 */
export const mailboxIsShown = (mailbox: Pick<MailboxRecord, 'role'>, show: boolean): boolean =>
  show || mailbox.role !== 'system';

/** The mailboxes the operator sees ({@link mailboxIsShown}), in the service's order. */
export const shownMailboxes = <M extends Pick<MailboxRecord, 'role'>>(mailboxes: readonly M[], show: boolean): M[] =>
  mailboxes.filter((m) => mailboxIsShown(m, show));

// --- Whether the brand's domain may have mail at all -----------------------------

/** The brand's mail domain: trimmed and lowercase, or the neutral fallback for a brand with no domain
 *  on record (the service is never asked about the fallback). */
export const mailDomainOf = (raw: string | undefined): string => (raw?.trim() || MAILBOX_FALLBACK_DOMAIN).toLowerCase();

/** What the domain read said about eligibility. `failed` says nothing either way. */
export type MailboxEligibilityRead = 'eligible' | 'not_eligible' | 'failed';

/**
 * A REJECTED domain read, as eligibility (a resolved one is `eligible`). `not_found` is a domain the
 * service has not registered yet, which the first create does: eligible. `not_eligible` is a domain
 * that will never have mailboxes. Anything else (the network, a 5xx, a refused token) is `failed`:
 * no answer to go by, so Mailbox stays hidden, as it does for `not_eligible`.
 */
export const mailboxEligibilityOf = (rejection: unknown): MailboxEligibilityRead => {
  if (!isMailboxError(rejection)) return 'failed';
  if (rejection.code === 'not_eligible') return 'not_eligible';
  return rejection.code === 'not_found' ? 'eligible' : 'failed';
};
